import { FC, useEffect } from 'react';
import { configApiRef, useApi } from '@mainstage/core-plugin-api';
import { Chip, ChipProps, Theme, makeStyles } from '@material-ui/core';
import { WbTooltip } from '../WbTooltip';
import clsx from 'clsx';
import { wbSafeNunjucksApiRef } from '../../apis';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    width: 'fit-content',
    background: `${theme.palette.softened.primary}`,
    '&:hover': {
      background: `${theme.palette.softened.primary}`,
    },
  },
  clickable: {
    '&:focus': {
      background: `${theme.palette.softened.primary}`,
    },
  },
  link: {
    textDecoration: 'underline',
  },
  label: {
    whiteSpace: 'nowrap',
  },
}));

export enum TagKind {
  generic = 'generic',
  schema = 'schema',
}

export interface Tag {
  tagFQN: string;
  description?: string;
  labelType?: string;
  state?: string;
  href?: string;
}

type WbTagProps = ChipProps & {
  tag: Tag;
  kind?: TagKind;
  tooltip?: boolean;
};

export const WbTag: FC<WbTagProps> = ({
  tag,
  kind = TagKind.generic,
  tooltip = true,
  onClick,
  ...props
}) => {
  const classes = useStyles();
  const configApi = useApi(configApiRef);
  const nunjucksApi = useApi(wbSafeNunjucksApiRef);

  const experimentalNunjucksRendering =
    useApi(configApiRef).getOptionalBoolean(
      'mesh.experimentalNunjucksRendering',
    ) ?? true;

  const displayTemplate = configApi.getOptionalString(
    `mesh.mapping.tag.${kind}.display`,
  );
  const tooltipTemplate = configApi.getOptionalString(
    `mesh.mapping.tag.${kind}.tooltip`,
  );

  useEffect(() => {
    let cleanupFn: any;
    // failsafe mechanism to disable new nunjucks rendering flow if something goes wrong
    if (experimentalNunjucksRendering) {
      cleanupFn = nunjucksApi.load(
        [displayTemplate, tooltipTemplate].filter(Boolean) as string[],
      );
    }
    return () => {
      if (cleanupFn) {
        cleanupFn();
      }
    };
  }, [
    displayTemplate,
    experimentalNunjucksRendering,
    nunjucksApi,
    tooltipTemplate,
  ]);

  function getTagDisplayValue<T>(
    inputTag: Tag,
    template: string | undefined,
    fallback: (t: Tag) => T,
  ) {
    if (template) {
      const rendered = nunjucksApi.render(template, inputTag);
      if (rendered) return rendered;
    }
    return fallback(inputTag);
  }

  const chip = (
    <Chip
      label={getTagDisplayValue(tag, displayTemplate, t => t.tagFQN)}
      size="small"
      classes={{
        root: classes.root,
        clickable: classes.clickable,
        label: tag.href ? clsx(classes.label, classes.link) : classes.label,
      }}
      clickable={Boolean(tag.href) || Boolean(onClick)}
      onClick={e => {
        if (tag.href) {
          e.preventDefault();
          e.stopPropagation();
          window.open(tag.href);
        } else onClick?.(e);
      }}
      {...props}
    />
  );

  const tooltipContent =
    getTagDisplayValue(tag, tooltipTemplate, t => t.description) ??
    getTagDisplayValue(tag, displayTemplate, t => t.tagFQN);

  return tooltip ? (
    <WbTooltip title={tooltipContent} interactive>
      {chip}
    </WbTooltip>
  ) : (
    chip
  );
};
